import { PathKind, SchemaPath, SchemaPathRules, validate } from '@angular/forms/signals';
import { spanEpoch, TimeSpanItem } from './chronologically-ordered.rule';

/** Options of the {@link coversSpan} rule. */
export interface CoversSpanConfig {
    /** message reported while any part of the span is unaccounted for */
    message?: string;
}

/** An outer span and the items that should fill it, as {@link coversSpan} reads them. */
export interface SpanCoverage {
    /** ISO instant, or '' when unset */
    start: string;
    /** ISO instant, or '' when unset */
    end: string;
    items: TimeSpanItem[];
}

/**
 * Signal-forms schema rule requiring a collection of time spans to account for every moment of an
 * outer span, leaving no hole at the start, in the middle or at the end.
 *
 * For the timesheet this replaces the legacy "unfilled mandatory gap" check: the row projection
 * renders a gap wherever a hole exists, and this rule is what stops the timesheet being submitted
 * while one is open. It reports one error for the form rather than one per hole — the gap rows
 * already show the user where they are.
 *
 * The coverage is read through `toCoverage` because the outer span and the items filling it are
 * three separate members of the validated value, under whatever names that value gives them —
 * the timesheet calls them `startOfWork`, `endOfWork` and `entries`. The projection only names
 * them; it converts nothing.
 *
 * The rule stays silent while the outer span is unset, since there is then nothing to cover and
 * `required` is the rule that should speak.
 *
 * @param path the value carrying the span and its items
 * @param toCoverage reads the outer span and the filling items as ISO instants
 * @param config optional message
 */
export function coversSpan<TValue, TPathKind extends PathKind = PathKind.Root>(
    path: SchemaPath<TValue, SchemaPathRules.Supported, TPathKind>,
    toCoverage: (value: TValue) => SpanCoverage,
    config?: CoversSpanConfig,
): void {
    validate(path, (context) => {
        const { start, end, items } = toCoverage(context.value());
        const spanStart = spanEpoch(start);
        const spanEnd = spanEpoch(end);

        if (spanStart === undefined || spanEnd === undefined) {
            return undefined;
        }

        const uncovered = { kind: 'uncoveredSpan', message: config?.message ?? '' };

        // Walk the items, requiring each to start no later than where the covered part so far ended.
        let covered = spanStart;
        for (const item of items) {
            const itemStart = spanEpoch(item.start);
            const itemEnd = spanEpoch(item.end);
            if (itemStart === undefined || itemEnd === undefined || itemStart > covered) {
                return uncovered;
            }
            covered = Math.max(covered, itemEnd);
        }

        return covered < spanEnd ? uncovered : undefined;
    });
}
